<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	title: 'Article Title',
	image: '/webclip.png',
	link: '/article',
	compact: false,
	authors: [{ name: 'Author Name', title: 'Prof.', image: '/webclip.png' }],
}
const schema = defineSchema({
	title: $.text('Title'),
	image: $.upload('Image'),
	link: $.text('Link'),
	compact: $.toggle('Compact'),
	authors: $.array(
		'Authors',
		defineSchema({
			name: $.text('Name'),
			title: $.text('Title'),
			image: $.upload('Image'),
		}),
		{ collapsible: true },
	),
})
export const ArticleCard = defineBlock({
	pickerHidden: true,
	thumbnail: '/block-thumbs/ArticleCard.svg',
	category: 'Content',
	order: 40,
	name: 'ArticleCard',
	label: 'Article Card',
	icon: 'article',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { Image } from '@bagelink/vue'
import { computed } from 'vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'

interface ArticleAuthor {
	name: string
	title: string
	image: string
	slug?: string
	link?: string
}
interface Article {
	title?: string
	image?: string
	link?: string
	authors?: ArticleAuthor[]
}




// `article` is an object convenience prop used by internal callers (Widgets,
// related-articles). The flat `title`/`image`/`link`/`authors` props are what
// the CMS editor edits. Flat props win; the object fills any gaps.
const props = defineProps<{
	title?: string
	image?: string
	link?: string
	compact?: boolean
	widget?: boolean
	authors?: ArticleAuthor[]
	article?: Article
}>()

const title = computed(() => props.title ?? props.article?.title ?? '')
const image = computed(() => props.image ?? props.article?.image ?? '/webclip.png')
const link = computed(() => props.link ?? props.article?.link ?? '/')
const authors = computed<ArticleAuthor[]>(() => props.authors ?? props.article?.authors ?? [])
const imageRatio = computed(() => (props.widget ? '3-2' : props.compact ? '4-3' : '16-9'))
</script>

<template>
	<article class="white hover" :class="{ compact, widget }">
		<RouterLink :to="link || '/'" class="decoration-none block">
			<Image :src="displayImage(image, 'card')" :alt="articleTitleText(title)" :ratio="imageRatio" class="w-100p cover" lazy />
			<h3
				class="pt-05 pb-025 my-0 regular balance"
				:class="compact ? 'txt18 line-height-13' : 'txt22 line-height-15'"
				v-html="sanitizeArticleTitleHtml(title)"
			/>
		</RouterLink>
		<AuthorSmallLink
			v-for="author in authors"
			:key="author.name"
			:show-image="false"
			:author="author"
		/>
	</article>
</template>
